import { useFormik } from 'formik';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import Select from 'react-select';
import { Card } from '@components/card/card';
import { CardBody, Form, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import useLookupCurrency from '@hooks/Lookups/use-Lookup-Currency';
import Breadcrumb from '@components/Shared/Breadcrumb';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import SwalAlert from '@components/Shared/Alert/SwalAlert';
import { useNavigate, useParams } from 'react-router-dom';
import ButtonPrint from '@components/Print/ButtonPrint';
import Loading from '@components/Shared/Loader/Loading';
import { useEffect } from 'react';

import {
  getPurchaseOrderById,
  cleanUpPurchaseOrderById,
  updatePurchaseOrder
} from '@store/purchases/PurchaseOrder/purchaseOrderSlice';
import POLines from './POLines';
import { TreeSelect } from 'antd';
import useGetSuppliersList from '@hooks/NTX/Peoples/useGetSuppliersList';
import useGetOperatingUnitWithPermission from '@hooks/NTX/Organization/useGetOperatingUnitWithPermission';
import useGetPaymentMethod from '@hooks/Payrolls/use-Get-PaymentMethod';
import useGetInventoryWithPermission from '@hooks/NTX/Organization/useGetInventoryWithPermission';
import { AddPurchaseOrderSchema } from '../../../ValidationForm/validationSchema';
import PoMultiInvLines from './PoMultiInvLines';
import { Can } from '@Permisstions/Can';
import { useDailyRate } from '@hooks/Finance/DailyRates/useDailyRate';

const toNum = (v: any) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (v: any) => {
  const n = Number(v || 0);
  return Math.round((n + Number.EPSILON) * 100) / 100;
};

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

const todayISO = () => new Date().toISOString().split('T')[0];

// ===============================
// ✅ حساب سطر (Line) قبل الإرسال
// ===============================
const calcLineSnapshot = (line: any) => {
  const price = round2(toNum(line?.price));
  const qty = round2(toNum(line?.qty));
  const vatRate = clamp(round2(toNum(line?.vatRate)), 0, 100);

  const totalBeforeDiscount = round2(price * qty);

  const rateVal = clamp(round2(toNum(line?.discountRate)), 0, 100);
  const amtValRaw = round2(toNum(line?.discountAmount));

  const mode = String(line?._discountMode || 'RATE');

  // ✅ fallback ذكي إذا ما تم إرسال _discountMode
  const useAmount =
    mode === 'AMOUNT' ||
    (mode !== 'RATE' && amtValRaw > 0 && rateVal === 0) ||
    (mode === 'RATE' && rateVal === 0 && amtValRaw > 0);

  let discountApplied = 0;

  if (useAmount) {
    discountApplied = clamp(amtValRaw, 0, totalBeforeDiscount);
  } else {
    const byRate = round2((totalBeforeDiscount * rateVal) / 100);
    discountApplied = clamp(byRate, 0, totalBeforeDiscount);
  }

  const amountAfterDiscount = round2(totalBeforeDiscount - discountApplied);
  const vatAmount = round2((amountAfterDiscount * vatRate) / 100);
  const totalWithVat = round2(amountAfterDiscount + vatAmount);

  return {
    vatRate,
    totalBeforeDiscount,
    discountApplied,
    total: amountAfterDiscount, // قبل VAT
    vatAmount,
    totalWithVat
  };
};

// =====================================
// ✅ خصم الهيدر ينعكس على VAT بدقة
// (نحسب Totals فقط - لا نعدل سطور الإرسال)
// =====================================
const calcHeaderTotalsAfterHeaderDiscount = (lines: any[], headerDiscountRate: any, headerDiscountAmount: any) => {
  const baseSum = round2(lines.reduce((s, l) => s + toNum(l.total), 0)); // مجموع قبل VAT بعد خصم السطور
  if (baseSum <= 0) {
    const vatSum0 = round2(lines.reduce((s, l) => s + toNum(l.vatAmount), 0));
    return { grossAmount: 0, vat: vatSum0, netAmount: round2(vatSum0) };
  }

  const hdrRate = clamp(round2(toNum(headerDiscountRate)), 0, 100);
  const hdrAmt = round2(toNum(headerDiscountAmount));

  // نحدد خصم الهيدر المطبق
  const headerDiscountApplied =
    hdrAmt > 0 ? clamp(hdrAmt, 0, baseSum) : hdrRate > 0 ? clamp(round2((baseSum * hdrRate) / 100), 0, baseSum) : 0;

  // إذا ما فيه خصم هيدر، نجمع مباشرة
  if (headerDiscountApplied <= 0) {
    const vatSum = round2(lines.reduce((s, l) => s + toNum(l.vatAmount), 0));
    const net = round2(baseSum + vatSum);
    return { grossAmount: baseSum, vat: vatSum, netAmount: net };
  }

  // توزيع خصم الهيدر على السطور بنسبتها لإعادة حساب VAT بدقة
  let remainingDisc = headerDiscountApplied;
  let subtotalAfterHdr = 0;
  let vatAfterHdr = 0;

  const lastIdx = lines.length - 1;

  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    const base = round2(toNum(l.total));
    if (base <= 0) continue;

    const share = i === lastIdx ? remainingDisc : round2((headerDiscountApplied * base) / baseSum);

    const discLine = clamp(share, 0, base);
    remainingDisc = round2(remainingDisc - discLine);

    const newBase = round2(base - discLine);
    const rate = clamp(round2(toNum(l.vatRate)), 0, 100);
    const newVat = round2((newBase * rate) / 100);

    subtotalAfterHdr = round2(subtotalAfterHdr + newBase);
    vatAfterHdr = round2(vatAfterHdr + newVat);
  }

  const net = round2(subtotalAfterHdr + vatAfterHdr);
  return { grossAmount: subtotalAfterHdr, vat: vatAfterHdr, netAmount: net };
};

const UpdatePurchaseOrder = () => {
  const { id, type } = useParams();
  const isMulti = type === 'multi';

  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { Currency, loading: loading6 } = useLookupCurrency();
  const { PaymentMethodList } = useGetPaymentMethod();

  const { OperatingUnitOptions, loading: loading7 } = useGetOperatingUnitWithPermission();
  const { InventoryOptions, loading: loading3 } = useGetInventoryWithPermission();

  const { treeSuppliersData, SuppliersList, loading: loading2 } = useGetSuppliersList();
  const { PurchaseOrderById: data, loading, error } = useAppSelector(state => state.PurchaseOrder);

  useEffect(() => {
    if (id) {
      dispatch(getPurchaseOrderById(id));
    }
    return () => {
      dispatch(cleanUpPurchaseOrderById());
    };
  }, [id]);

  //#region Formik
  const formik = useFormik({
    initialValues: {
      isMulti: isMulti,
      id: data ? data.id : '',
      // no: data ? data.no : '',
      code: data ? data.code : '',
      // name: data ? data.name : '',
      // ✅ جديد
      supplierInvoiceNum: data ? data.supplierInvoiceNum : '',
      // name2: data ? data.name2 : '',
      // transTypeId: data ? data.transTypeId : '',
      organizationId: data ? data.organizationId : '',
      peopleId: data ? data.peopleId : '',
      recipientname: data ? data.recipientname : '',
      billToLocationId: data ? data.billToLocationId : '',
      shipToLocationId: data ? data.shipToLocationId : '',
      referenceId: data ? data.referenceId : '',
      transactionDate: data ? data.transactionDate?.split('T')[0] : '',
      deliveryDate: data ? data.deliveryDate?.split('T')[0] : '',
      expirationDate: data ? data.expirationDate?.split('T')[0] : '',
      dueDate: data ? data.dueDate?.split('T')[0] : '',
      paymentTermsId: data ? data.paymentTermsId : '',
      description: data ? data.description : '',
      grossAmount: data ? data.grossAmount : '',
      discountRate: data ? data.discountRate : '',
      discountAmount: data ? data.discountAmount : '',
      vat: data ? data.vat : '',
      netAmount: data ? data.netAmount : '',
      isAme: true,
      ameStatus: data ? data.ameStatus : 0,
      personId: data ? data.personId : '',
      contractId: data ? data.contractId : '',
      paymentTerms: data ? data.paymentTerms : '',
      deliveryTerm: data ? data.deliveryTerm : '',
      inventoryId: data ? data.inventoryId : '',
      waybill: data ? data.waybill : '',
      phone: data ? data.phone : '',
      currencyCode: data ? data.currencyCode : '',
      currencyConversionRate: data ? data.currencyConversionRate : '',
      batchId: data ? data.batchId : '',
      paymentMethodId: data ? data.paymentMethodId : '',
      // transactionsLinesList:
      //   data?.transactionsLinesList?.map((item : any) => ({
      //     id: item.id,
      //     // transactionId: item.transactionId,
      //     itemId: item.itemId,
      //     lineNumber: item.lineNumber,
      //     uom: item.uom,
      //     description: item.description,
      //     price: item.price,
      //     qty: item.qty,
      //     discountRate: item.discountRate,
      //     discountAmount: item.discountAmount,
      //     vatRate: item.vatRate,
      //     total: item.total,
      //     vatAmount: item.vatAmount,
      //     inventoryId: item.inventoryId,
      //     itemType: item.itemType
      //   })) || []

      transactionsLinesList:
        data?.transactionsLinesList?.map(item => {
          const inferredMode = toNum(item.discountAmount) > 0 && toNum(item.discountRate) === 0 ? 'AMOUNT' : 'RATE';

          const snap = calcLineSnapshot({ ...item, _discountMode: inferredMode });

          return {
            id: item.id,
            itemId: item.itemId,
            lineNumber: item.lineNumber,
            uom: item.uom,
            description: item.description,
            price: item.price,
            qty: item.qty,

            discountRate: item.discountRate,
            discountAmount: item.discountAmount,
            _discountMode: inferredMode,

            vatRate: item.vatRate,

            // ✅ ثبّت القيم الصحيحة للعرض والحساب
            totalBeforeDiscount: snap.totalBeforeDiscount,
            total: snap.total, // قبل الضريبة
            vatAmount: snap.vatAmount,
            totalWithVat: snap.totalWithVat, // شامل الضريبة
            _discountAppliedAmount: snap.discountApplied,

            inventoryId: item.inventoryId,
            itemType: item.itemType
          };
        }) || []
    },

    enableReinitialize: true,
    validationSchema: AddPurchaseOrderSchema(t),
    onSubmit: values => {
      const rawLines = values.transactionsLinesList || [];

      // ✅ 1) Normalize lines: إعادة حساب + تثبيت discountAmount الحقيقي + lineNumber
      const normalizedLines = rawLines.map((line: any, idx: number) => {
        const snap = calcLineSnapshot(line);
        return {
          ...line,
          lineNumber: idx + 1,
          totalBeforeDiscount: snap.totalBeforeDiscount,
          total: snap.total, // قبل VAT (بعد خصم السطر)
          vatRate: snap.vatRate,
          vatAmount: snap.vatAmount,
          totalWithVat: snap.totalWithVat,
          _discountAppliedAmount: snap.discountApplied,
          discountAmount: snap.discountApplied // ✅ المعتمد
        };
      });

      // ✅ 2) Header totals: تعيد حساب VAT بدقة إذا يوجد خصم هيدر
      const headerTotals = calcHeaderTotalsAfterHeaderDiscount(
        normalizedLines,
        values.discountRate,
        values.discountAmount
      );

      if (normalizedLines.length === 0) {
        SwalAlert({ text: 'أدخل أصناف' });
        formik.setSubmitting(false);
        return;
      }

      if (data.hasInventoryTransaction) {
        SwalAlert({ text: 'لا يمكن تعديل أمر الشراء بعد إجراء عمليات مخزنية عليه.' });
        formik.setSubmitting(false);
        return;
      }

      dispatch(
        updatePurchaseOrder({
          isMultiInventoryFlag: isMulti,
          id: values.id,
          organizationId: values.organizationId,
          peopleId: values.peopleId,
          recipientname: values.recipientname,
          billToLocationId: billToLocationId,
          shipToLocationId: shipToLocationId,
          referenceId: values.referenceId,
          transactionDate: values.transactionDate,
          deliveryDate: values.deliveryDate || null,
          expirationDate: values.expirationDate || null,
          dueDate: values.dueDate || null,
          paymentTermsId: paymentTermsId,
          description: values.description,
          supplierInvoiceNum: values.supplierInvoiceNum,

          grossAmount: headerTotals.grossAmount,
          discountRate: toNum(values.discountRate) || 0,
          discountAmount: toNum(values.discountAmount) || 0,
          vat: headerTotals.vat,
          netAmount: headerTotals.netAmount,

          contractId: values.contractId || null,
          personId: values.personId,
          paymentTerms: values.paymentTerms,
          deliveryTerm: values.deliveryTerm,
          inventoryId: values.inventoryId,
          waybill: values.waybill,
          phone: values.phone,
          currencyCode: values.currencyCode,
          currencyConversionRate: toNum(values.currencyConversionRate) || 1,
          batchId: values.batchId || null,
          paymentMethodId: values.paymentMethodId || null,

          transactionsLinesList: normalizedLines.map((item: any) => ({
            id: item.id,
            transactionId: data.id,
            itemId: item.itemId || null,
            lineNumber: item.lineNumber,
            uom: item.uom,
            description: item.description,
            price: toNum(item.price),
            qty: toNum(item.qty),
            discountRate: toNum(item.discountRate),
            discountAmount: toNum(item.discountAmount),
            vatRate: toNum(item.vatRate),
            total: toNum(item.total),
            vatAmount: toNum(item.vatAmount),
            inventoryId: isMulti ? item.inventoryId : values.inventoryId,
            itemType: item.itemType
          }))
        })
      )
        .unwrap()
        .then((res: any) => {
          if (res?.succeeded === true) {
            CustomAlert({ action: 'Edit' });
            navigate('/PUR/PurchaseOrder', { replace: true });
          } else {
            formik.setSubmitting(false);
            CustomAlert({ action: 'Error' });
          }
        })
        .catch(() => {
          formik.setSubmitting(false);
          CustomAlert({ action: 'Error' });
        });
    }
  });

  const { values, handleChange: formikhandleChange, errors, touched } = formik;
  //#endregion

  const { setFieldValue, setFieldTouched } = formik;

  const {
    rate,
    loading: loadingRate,
    error: errorRate,
    refetch
  } = useDailyRate({
    currencyCode: values.currencyCode,
    date: values.transactionDate
  });

  // ✅ اربط rate داخل الفورم
  useEffect(() => {
    if (!values.currencyCode) {
      if (toNum(values.currencyConversionRate) !== 1) setFieldValue('currencyConversionRate', 1, false);
      return;
    }

    if (!loadingRate && !errorRate) {
      const newRate = toNum(rate) || 1;
      if (toNum(values.currencyConversionRate) !== newRate) {
        setFieldValue('currencyConversionRate', newRate, false);
      }
    }
  }, [
    values.currencyCode,
    values.transactionDate,
    values.currencyConversionRate,
    loadingRate,
    errorRate,
    rate,
    setFieldValue
  ]);

  const Supplier = SuppliersList?.find(f => f.id === values.peopleId);
  const paymentTermsId = Supplier?.termsId;
  const shipToLocationId = Supplier?.shipToLocationId;
  const billToLocationId = Supplier?.billToLocationId;

  const NotEditable = !!data?.hasInventoryTransaction;

  return (
    <div>
      <div className='d-flex align-items-center'>
        <Breadcrumb
          BreadcrumbList={[
            {
              name: 'Home',
              link: ':moduleHome'
            },
            {
              name: 'PurchaseOrder',
              link: '/PUR/PurchaseOrder'
            },
            {
              name: 'تعديل أمر الشراء',
              link: null
            }
          ]}
          PageName='تعديل أمر الشراء'
        />
        <div className='ms-auto'>
          <div className='col-5 col-md-6 text-end'>
            <ButtonPrint
              PrintTemplateTypeId='F2274073-1099-4F9D-BC7F-ADE744CA711E' // قوالب امر شراء
              ReferenceId={id || ''}
            />
          </div>
        </div>
      </div>

      <Loading loading={loading}>
        <Form onSubmit={formik.handleSubmit}>
          <Card className='shadow mb-3'>
            <CardBody>
            {/* {NotEditable && (
              <div className='alert alert-warning d-flex align-items-center   border-0 rounded-3'>
                <i className='fas fa-lock fa-2x me-3'></i>

                <div>
                  <h6 className='mb-0 fw-bold'>{t('السجل مقفل')}</h6>
                  <small>{t('لا يمكن التعديل بسبب وجود حركات مخزنية مرتبطة')}</small>
                </div>
              </div>
            )} */}
              <div className='row'>
                <div className='col-lg-3'>
                  <div className='form-group flex-nowrap mb-3'>
                    <label className='form-label'>{t('رقم أمر الشراء')}</label>

                    <div className='input-group flex-nowrap'>
                      <Form.Control
                        readOnly
                        className='form-control fw-bold text-center   text-theme'
                        type='text'
                        autoComplete='off'
                        onChange={formikhandleChange}
                        value={values.code || ''}
                        isInvalid={!!(touched.code && errors.code)}
                      />
                    </div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <label className='form-label'>{t('تاريخ الأمر')}</label>
                  <input
                    type='date'
                    className='form-control'
                    name='transactionDate'
                    value={values.transactionDate || todayISO()}
                    onChange={formikhandleChange}
                  />
                  {touched.transactionDate && errors.transactionDate && (
                    <div className='text-danger'>{String(errors.transactionDate)}</div>
                  )}
                </div>

                <div className='col-md-3 '>
                  <label className='form-label'>{t('DeliveryDate')}</label>
                  <input
                    type='date'
                    className='form-control'
                    name='deliveryDate'
                    value={values.deliveryDate || ''}
                    onChange={formikhandleChange}
                  />
                </div>

                <div className='col-md-3'>
                  <label className='form-label'>{t('DueDate')}</label>
                  <input
                    type='date'
                    className='form-control'
                    name='dueDate'
                    value={values.dueDate || ''}
                    onChange={formikhandleChange}
                  />
                </div>
                <div className='col-md-3 mb-2'>
                  <label className='form-label'>{t('OperatingUnit')}</label>
                  <Select
                    classNamePrefix='react-select'
                    options={OperatingUnitOptions}
                    value={OperatingUnitOptions?.find((x: any) => x.value === values.organizationId) || null}
                    onChange={(opt: any) => formik.setFieldValue('organizationId', opt?.value || '', true)}
                    isLoading={loading7}
                  />
                  {touched.organizationId && errors.organizationId && (
                    <div className='text-danger'>{String(errors.organizationId)}</div>
                  )}
                </div>

                <div className='col-md-3 mb-2'>
                  <label className='form-label'>{t('Inventory')}</label>
                  <Select
                    classNamePrefix='react-select'
                    options={InventoryOptions}
                    value={InventoryOptions?.find((x: any) => x.value === values.inventoryId) || null}
                    onChange={(opt: any) => formik.setFieldValue('inventoryId', opt?.value || '', true)}
                    isLoading={loading3}
                    isDisabled={isMulti}
                  />
                  {touched.inventoryId && errors.inventoryId && (
                    <div className='text-danger'>{String(errors.inventoryId)}</div>
                  )}
                </div>

                <div className='col-md-3 mb-2'>
                  <label className='form-label'>{t('Supplier')}</label>
                  <TreeSelect
                    style={{ width: '100%' }}
                    treeData={treeSuppliersData}
                    value={values.peopleId}
                    onChange={(val: any) => {
                      formik.setFieldValue('peopleId', val || '', true);
                    }}
                    placeholder={t('Select')}
                    allowClear
                    showSearch
                    treeDefaultExpandAll={false}
                  />
                  {touched.peopleId && errors.peopleId && <div className='text-danger'>{String(errors.peopleId)}</div>}
                </div>

                <div className='col-lg-3'>
                  <div className='form-group flex-nowrap mb-3'>
                    <label className='form-label'>{t('الرقم المرجعي')}</label>

                    <div className='input-group flex-nowrap'>
                      <Form.Control
                        className='form-control text-center'
                        type='text'
                        autoComplete='off'
                        name='supplierInvoiceNum'
                        onChange={formikhandleChange}
                        value={values.supplierInvoiceNum || ''}
                        isInvalid={!!(touched.supplierInvoiceNum && errors.supplierInvoiceNum)}
                        // placeholder={i18n.language === 'ar' ? 'مثال: INV-12345' : 'e.g. INV-12345'}
                      />
                    </div>

                    {touched.supplierInvoiceNum && errors.supplierInvoiceNum && (
                      <div className='text-danger'>{String(errors.supplierInvoiceNum)}</div>
                    )}
                  </div>
                </div>

                <div className='col-md-2  '>
                  <div className='form-group mb-3'>
                    <label className='form-label'>{t('PaymentMethod')}</label>
                    <Form.Select
                      className='form-select'
                      name='paymentMethodId'
                      onChange={formik.handleChange}
                      value={values.paymentMethodId}
                      isInvalid={!!(touched.paymentMethodId && errors.paymentMethodId)}
                    >
                      <option key={null} value={''}>
                        {i18n.language === 'ar' ? 'إختر' : 'Choose'}
                      </option>
                      {PaymentMethodList?.map((item: any) => (
                        <option key={item.id} value={item.id}>
                          {i18n.language === 'ar' ? item.name2 : item.name}
                        </option>
                      ))}
                    </Form.Select>
                    {touched.paymentMethodId && errors.paymentMethodId && (
                      <div className='text-danger'>{errors.paymentMethodId as any}</div>
                    )}
                  </div>
                </div>

                <div className='col-lg-2'>
                  <div className='form-group mb-3'>
                    <label className='form-label'> {t('Currency')} </label>
                    <Form.Select
                      className='form-select'
                      name='currencyCode'
                      onChange={formik.handleChange}
                      value={values.currencyCode || ''}
                      isInvalid={!!(touched.currencyCode && errors.currencyCode)}
                    >
                      <option value=''>{i18n.language === 'ar' ? 'إختر' : 'Choose'}</option>

                      {Currency?.map((item: any, idx: number) => (
                        <option key={idx} value={item.currencyCode}>
                          {item.description}
                        </option>
                      ))}
                    </Form.Select>
                    {touched.currencyCode && errors.currencyCode && (
                      <div className='text-danger'>{errors.currencyCode as any}</div>
                    )}
                  </div>
                </div>

                <div className='col-lg-2'>
                  <div className='form-group mb-3'>
                    <label className='form-label'> {t('CurrencyConversionRate')} </label>
                    <InputGroup>
                      <Form.Control
                        className='form-control text-center'
                        type='text'
                        readOnly
                        value={loadingRate ? '' : errorRate ? 'فشل التحميل' : values.currencyConversionRate}
                        placeholder={loadingRate ? 'جاري التحميل...' : ''}
                      />

                      {loadingRate && (
                        <InputGroup.Text>
                          <div className='spinner-border spinner-border-sm text-primary'></div>
                        </InputGroup.Text>
                      )}

                      {errorRate && !loadingRate && (
                        <InputGroup.Text style={{ padding: '0' }}>
                          <button
                            type='button'
                            className='btn btn-outline-warning h-100 px-2'
                            onClick={refetch}
                            title='إعادة تحميل سعر الصرف'
                          >
                            <i className='fas fa-redo'></i>
                          </button>
                        </InputGroup.Text>
                      )}
                    </InputGroup>

                    {errorRate && !loadingRate && (
                      <small className='text-warning mt-1 d-block text-center'>
                        <i className='fas fa-exclamation-circle me-1'></i>
                        تعذر تحميل السعر
                        <button type='button' className='btn btn-link btn-sm p-0 text-warning ms-1' onClick={refetch}>
                          (إعادة المحاولة)
                        </button>
                      </small>
                    )}
                  </div>
                </div>

                <div className='col-md-6  '>
                  <label className='form-label'>{t('ملاحظـات')}</label>
                  <input
                    type='text'
                    className='form-control'
                    name='description'
                    value={values.description || ''}
                    onChange={formikhandleChange}
                  />
                </div>
              </div>
            </CardBody>

            {/* <CardFooter className='d-flex justify-content-end gap-1'>
              <ButtonPrint />
            </CardFooter> */}
          </Card>

          <Card className='shadow mb-3'>
            {data &&
              Object.keys(data)?.length > 0 &&
              (isMulti ? <PoMultiInvLines formik={formik} /> : <POLines formik={formik} isUpdate={true} />)}

            {formik.errors.transactionsLinesList?.length && <div className='text-danger'> * مطلوب </div>}
          </Card>

          <Card className='p-2'>
            <div className='text-end '>
              <Can permission={'PUREditPurchaseOrder'}>
                <button
                  disabled={formik.isSubmitting}
                  type='submit'
                  className='btn btn-lg me-1 btn-theme mb-1 rounded-0'
                >
                  {formik.isSubmitting ? (
                    <>
                      <i className='fa fa-spinner fa-spin me-1'></i> {t('Saving')}
                    </>
                  ) : (
                    <>
                      <i className='fa fa-save fa-fw me-1'></i> {t('Save')}
                    </>
                  )}
                </button>
              </Can>

              <button
                type='button'
                className=' btn btn-lg me-1 btn-default mb-1 rounded-0'
                onClick={() => navigate('/PUR/PurchaseOrder', { replace: true })}
              >
                <i className='fa fa-power-off fa-fw me-1'></i> {t('Cancel')}
              </button>
            </div>
          </Card>
        </Form>
      </Loading>
    </div>
  );
};

export default UpdatePurchaseOrder;
